import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { dayjs } from '@karve.it/core';
import { Store, createSelector } from '@ngrx/store';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { Delta } from 'quill/core';
import { combineLatest, distinctUntilChanged, filter, map, startWith, switchMap, take, tap } from 'rxjs';
import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { JobEventStatus, MAX_32_BIT_INT, eventTypeInfoMapV2 } from 'src/app/global.constants';
import { convertCentsToDollars } from 'src/app/lib.ts/currency.util';
import { DetailsHelperService } from 'src/app/services/details-helper.service';
import { FreyaHelperService } from 'src/app/services/freya-helper.service';
import { ResponsiveHelperService } from 'src/app/services/responsive-helper.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { scrollIntoViewAndHighlight } from 'src/app/utilities/element.util';
import { SubSink } from 'subsink';

import { Charge, Discount, FullJobFragment } from '../../../../generated/graphql.generated';
import { zonesFeature } from '../../../core/zones/zones.reducer';
import { DISPATCH_PERMISSIONS } from '../../../permissions.constants';
import { DispatchEditEventComponent } from '../../../schedules/dispatch/components/dispatch-edit-event/dispatch-edit-event.component';
import { DispatchActions } from '../../../schedules/dispatch/store/dispatch.actions';
import { DispatchEvent } from '../../../schedules/dispatch/store/dispatch.reducer';
import { selectUsersLoaded } from '../../../schedules/dispatch/store/dispatch.selectors';
import { createConfigSelector } from '../../../state/branding.store';
import { selectFeature } from '../../../state/featureFlags.store';
import { AddDiscountsModalComponent } from '../../add-discounts-modal/add-discounts-modal.component';
import { AddEventButtonComponent } from '../../add-event-button/add-event-button.component';
import { deletableEventStatuses } from '../../job-charges-helpers';
import { validateLocationsForEventType, getPromoteEventInfo } from '../../job-helpers';
import {
    jobToolFeature,
    selectJob,
    selectJobInCurrentZone,
    selectJobLoading,
    selectJobUpdating,
} from '../../job-tool.reducer';
import { AddChargesComponent } from '../add-charges/add-charges.component';
import { JobFinancialsTableComponent } from '../financials-table/financials-table.component';
import { WorkOrdersActions } from '../job-workorders.actions';
import { selectCancelledEvents, selectWorkorders } from '../job-workorders.component';
import { jobWorkordersFeature } from '../job-workorders.feature';
import { workOrdersSelectors } from '../job-workorders.selectors';

@Component({
    selector: 'app-events-table',
    standalone: true,
    imports: [
        SharedModule,
        FreyaCommonModule,
        AddChargesComponent,
        AddDiscountsModalComponent,
        AddEventButtonComponent,
        JobFinancialsTableComponent,
        DispatchEditEventComponent,
        QuillEditorComponent,
    ],
    templateUrl: './events-table.component.html',
    styleUrls: [
        './events-table.component.scss',
        '../../job-financials/document-container-style.scss',
    ],
})
export class EventsTableComponent implements OnDestroy, AfterViewInit {

    protected readonly DISPATCH_PERMISSIONS = DISPATCH_PERMISSIONS;

    @ViewChildren('workorderRef') workorderQueryList!: QueryList<ElementRef<HTMLElement>>;

    constructor(
        public store: Store,
        public freyaHelper: FreyaHelperService,
        public responsiveHelper: ResponsiveHelperService,
        private detailsHelper: DetailsHelperService,
        private elementRef: ElementRef,
        private route: ActivatedRoute,
        private ngZone: NgZone,
    ) {}

    public eventTypeInfoMap = eventTypeInfoMapV2;

    public actions = WorkOrdersActions;

    crewEditDialogVisible = false;
    eventForCrewEdit: DispatchEvent | null = null;
    editingEventId: string | null = null;

    openEditCrew(workorder: any): void {
        this.editingEventId = workorder.eventId;

        this.subs.sink = this.store.select(selectUsersLoaded).pipe(take(1)).subscribe((loaded) => {
            if (!loaded) this.store.dispatch(DispatchActions.initCrew());
        });

        this.subs.sink = this.store.select(selectUsersLoaded).pipe(filter(Boolean), take(1))
            .subscribe(() => {
                this.eventForCrewEdit = {
                    event: {
                        id: workorder.eventId,
                        attendees: workorder.rawAttendees || [],
                    } as any,
                    crew: (workorder.crew || []).map((a: any) => ({
                        ...a,
                        name: a.name || [a.user?.familyName, a.user?.givenName].filter(Boolean).join(', '),
                    })),
                    trucks: [],
                    customer: { name: workorder.title } as any,
                    startTime: workorder.start ? dayjs(workorder.start * 1000).format('h:mm A') : '',
                    endTime: '',
                    style: { backgroundColor: '', color: '' },
                    eventHasConflict: false,
                    crewRequirement: 0,
                    needsMoreCrew: false,
                    laborCharge: { name: '', color: '' },
                    totalLaborCost: 0,
                    laborPercentage: 0,
                };
                this.editingEventId = null;
                this.crewEditDialogVisible = true;
            });
    }

    closeCrewEditDialog(): void {
        this.crewEditDialogVisible = false;
        this.eventForCrewEdit = null;
    }

    // ---- Event Notes (per-event, mover-facing) -----------------------------
    // Ray: an archive of what was done per event — collapsible, rich text,
    // included in crew comms. Stored on event metadata.eventNotes as a
    // serialized Quill delta (same convention as the dispatch dayNotes).
    public notesExpanded: Record<string, boolean> = {};

    public notesDeltas = new Map<string, Delta>();

    public eventNotesModules: QuillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        ],
    };

    public toggleNotes(eventId: string): void {
        this.notesExpanded[eventId] = !this.isNotesExpanded(eventId);
        this.persistNotesExpansion(eventId);
    }

    public isNotesExpanded(eventId: string): boolean {
        this.loadNotesExpansion();
        return this.notesExpanded[eventId] ?? true;
    }

    // ---- Event Notes collapse persistence (Ray: once collapsed, stays
    // collapsed until someone manually reopens it — survives reloads) ----
    private static readonly NOTES_EXPANDED_KEY = 'workorders.eventNotesExpanded';
    private notesExpandedLoaded = false;

    private loadNotesExpansion(): void {
        if (this.notesExpandedLoaded) return;
        this.notesExpandedLoaded = true;
        try {
            const raw = localStorage.getItem(EventsTableComponent.NOTES_EXPANDED_KEY);
            if (raw) this.notesExpanded = JSON.parse(raw) ?? {};
        } catch {
            // Corrupt state — keep the (expanded) default.
        }
    }

    private persistNotesExpansion(eventId: string): void {
        try {
            // Re-read + merge instead of writing the cached map wholesale:
            // with several instances open (a second tab or view) a full-map
            // write would roll back another event's saved state.
            const raw = localStorage.getItem(EventsTableComponent.NOTES_EXPANDED_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            const latest: Record<string, boolean> =
                parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                    ? { ...parsed }
                    : {};
            latest[eventId] = this.notesExpanded[eventId];
            this.notesExpanded = { ...this.notesExpanded, ...latest };
            localStorage.setItem(EventsTableComponent.NOTES_EXPANDED_KEY, JSON.stringify(latest));
        } catch {
            // Storage full/unavailable — collapse state stays session-only.
        }
    }

    public getNotesDelta(eventId: string, serialized: string): Delta {
        // Memoize so ngModel doesn't receive a new reference each CD cycle
        if (this.notesDeltas.has(eventId)) {
            return this.notesDeltas.get(eventId)!;
        }
        if (serialized) {
            try {
                const parsed = JSON.parse(serialized);
                if (parsed?.ops) {
                    const delta = new Delta(parsed.ops);
                    this.notesDeltas.set(eventId, delta);
                    return delta;
                }
            } catch {
                // Legacy plain-text content — wrap it so the words survive
                // instead of discarding the note.
                const legacy = new Delta().insert(serialized);
                this.notesDeltas.set(eventId, legacy);
                return legacy;
            }
        }
        const empty = new Delta();
        this.notesDeltas.set(eventId, empty);
        return empty;
    }

    public onNotesChanged(eventId: string, delta: Delta): void {
        const serialized = JSON.stringify({ ops: delta.ops });
        // Show the typed content immediately (local memo)
        this.notesDeltas.set(eventId, delta);
        this.store.dispatch(this.actions.notesChanged({ eventId, eventNotes: serialized }));
    }

    resizeObserver: ResizeObserver;

    intersectionObserver: IntersectionObserver;

    subs = new SubSink();

    width: number | undefined;

    subtotalLimit = MAX_32_BIT_INT;

    private _selectChargeActions = createSelector(
        jobWorkordersFeature.selectWorkordersEditor,
        selectWorkorders,
        ({ actionsMenuChargeId }, workorders) => {
            if (!actionsMenuChargeId) {
                return [];
            }

            const eventWithCharge = workorders?.find(workorder =>
                workorder.charges.some(charge => charge.chargeId === actionsMenuChargeId)
            );

            if (!eventWithCharge) {
                return [];
            }

            return [
                {
                    label: 'Remove',
                    icon: 'pi pi-trash',
                    command: () => this.store.dispatch(this.actions.removeChargeMenuItemClicked({ chargeId: actionsMenuChargeId })),
                },
                {
                    label: 'Edit',
                    icon: 'pi pi-pencil',
                    command: () => this.store.dispatch(this.actions.editChargeButtonClicked(
                        {
                            chargeId: actionsMenuChargeId,
                            eventId: eventWithCharge?.eventId,
                        })),
                }
            ];
        }
    );

    private _selectEventActions = createSelector(
        selectJob,
        selectJobLoading,
        jobWorkordersFeature.selectWorkordersEditor,
        selectWorkorders,
        (job, jobLoading, { actionsMenuEventId }, workorders) => {

            if (!actionsMenuEventId) {
                return [];
            }

            const workorderIndex = workorders.findIndex((e) => e.eventId === actionsMenuEventId);

            if (workorderIndex < 0) {
                return [];
            }

            const event = workorders[workorderIndex];

            const deleteAndCancelDisabled = jobLoading || event.locked || event.invoiced;

            const promoteEventInfo = getPromoteEventInfo(event);

            return [
                {
                    label: 'Move To Top',
                    icon: 'pi pi-arrow-circle-up',
                    visible: workorderIndex > 0,
                    command: () => this.store.dispatch(this.actions.moveToTopMenuItemClicked({ eventId: actionsMenuEventId })),
                },
                {
                    label: 'Move Up',
                    icon: 'pi pi-arrow-up',
                    visible: workorderIndex > 0,
                    command: () => this.store.dispatch(this.actions.moveUpMenuItemClicked({ eventId: actionsMenuEventId })),
                },
                {
                    label: 'Move Down',
                    icon: 'pi pi-arrow-down',
                    visible: workorderIndex < workorders.length - 1,
                    command: () => this.store.dispatch(this.actions.moveDownMenuItemClicked({ eventId: actionsMenuEventId })),
                },
                {
                    label: 'Move To Bottom',
                    icon: 'pi pi-arrow-circle-down',
                    visible: workorderIndex < workorders.length - 1,
                    command: () => this.store.dispatch(this.actions.moveToBottomMenuItemClicked({ eventId: actionsMenuEventId })),
                },
                {
                    label: [ 'booked', 'confirmed', 'completed' ].includes(event.status) ? 'Reschedule' : 'Book',
                    icon: 'pi pi-calendar-plus',
                    command: () => this.store.dispatch(this.actions.scheduleEventMenuItemClicked()),
                    visible: !event.hasUnsavedChanges,
                    disabled: event.bookButtonDisabled,
                },
                {
                    label: 'Save',
                    icon: 'pi pi-save',
                    command: () => this.store.dispatch(this.actions.saveMenuItemClicked()),
                    visible: event.hasUnsavedChanges,
                },
                {
                    label: promoteEventInfo.label,
                    icon: promoteEventInfo.icon,
                    command: () => this.store.dispatch(this.actions.promoteEventMenuItemClicked()),
                    visible: promoteEventInfo.visible,
                },
                {
                    label: 'Duplicate',
                    icon: 'pi pi-clone',
                    command: () => this.store.dispatch(this.actions.duplicateMenuItemClicked()),
                },
                {
                    id: 'cancel',
                    label: 'Cancel',
                    icon: 'pi pi-ban',
                    command: () => this.store.dispatch(this.actions.cancelMenuItemClicked({ eventId: actionsMenuEventId })),
                    disabled: deleteAndCancelDisabled,
                    visible: !deletableEventStatuses.includes(event.status as JobEventStatus),
                },
                {
                    id: 'delete',
                    label: 'Delete',
                    icon: 'pi pi-trash',
                    command: () => this.store.dispatch(this.actions.deleteMenuItemClicked({ eventId: actionsMenuEventId })),
                    disabled: deleteAndCancelDisabled || Boolean(job.closedAt),
                    visible: deletableEventStatuses.includes(event.status as JobEventStatus),
                },
                {
                    label: 'View History',
                    icon: 'pi pi-book',
                    command: () => this.store.dispatch(this.actions.viewHistoryMenuItemClicked()),
                    visible: !event.new,
                },
                {
                    id: 'share',
                    label: 'Share',
                    icon: 'pi pi-arrow-up-right',
                    command: () => this.store.dispatch(this.actions.shareMenuItemClicked()),
                }
            ];
        }
    );

    private _selectDiscountActions = createSelector(
        jobWorkordersFeature.selectWorkordersEditor,
        jobWorkordersFeature.selectAvailableDiscounts,
        selectJob,
        ({ actionsMenuAppliedDiscountId, workorders }, availableDiscounts, job) => {

            if (!actionsMenuAppliedDiscountId) { return []; }

            const editableDiscounts = workorders.flatMap((wo) => wo.discounts);

            const editableDiscount = editableDiscounts?.find((ad) => ad.appliedId === actionsMenuAppliedDiscountId);

            const jobDiscounts = job?.events
                .flatMap((e) => e.discounts)
                .map((d) => d?.discount) || [];

            const discount = [ ...jobDiscounts, ...availableDiscounts ].find((d) => d.id === editableDiscount?.discountId);

            const hasCustomRange = discount?.customAmountRange?.length > 0;

            return [
                {
                    label: 'Edit',
                    icon: 'pi pi-pencil',
                    command: () => {},
                    disabled: true,
                    tooltipOptions: {
                        tooltipLabel: !hasCustomRange && 'Discount wasn\'t given a custom amount range',
                        tooltipPosition: 'left',
                    }
                },
                {
                    label: 'Remove',
                    icon: 'pi pi-trash',
                    command: () => {
                        const eventWithDiscount = workorders?.find(workorder =>
                            workorder.discounts.some(d => d.appliedId === actionsMenuAppliedDiscountId)
                        );
                        if (eventWithDiscount && actionsMenuAppliedDiscountId) {
                            this.store.dispatch(this.actions.removeDiscountMenuItemClicked({
                                eventId: eventWithDiscount.eventId,
                                appliedId: actionsMenuAppliedDiscountId
                            }));
                        }
                    },
                }
            ];
        }
    );

    public viewModel$ = this.store.select(createSelector({
        workorders: selectWorkorders,
        cancelledEvents: selectCancelledEvents,
        eventActions: this._selectEventActions,
        chargeActions: this._selectChargeActions,
        discountActions: this._selectDiscountActions,
        validEventIds: createSelector(workOrdersSelectors.selectEventIds, ({ validEventIds }) => validEventIds),
        collapsed: createSelector(jobWorkordersFeature.selectWorkordersEditor, ({ collapsed }) => collapsed),
        isAllEventsCollapsed: createSelector(
            selectWorkorders,
            jobWorkordersFeature.selectWorkordersEditor,
            (eventsWithCharges, { collapsed }) => eventsWithCharges.every((wo) => collapsed[wo.eventId]),
        ),
        job: selectJob,
        jobLoading: selectJobLoading,
        jobBeingUpdated: selectJobUpdating,
        jobLoadingOrUpdating: createSelector(
            selectJobLoading,
            selectJobUpdating,
            (loading, updating) => loading || updating,
        ),
        aiGenerateChargesEnabled: createSelector(
            selectFeature('ai-generate-charges'),
            createConfigSelector('PRICINGENGINE_ENABLED'),
            (feature, configEnabled) => feature && configEnabled,
        ),
        defaultDiscount: createSelector(
            jobWorkordersFeature.selectAvailableDiscounts,
            (availableDiscounts) => {
                const [ firstAvailableDiscount ] = availableDiscounts;
                if (!firstAvailableDiscount) {
                    return { id: undefined as string | undefined, amount: undefined as number | undefined };
                }
                const amount = firstAvailableDiscount.discountType === 'percentage'
                    ? firstAvailableDiscount.amount
                    : convertCentsToDollars(firstAvailableDiscount.amount, true);
                return { id: firstAvailableDiscount.id, amount };
            },
        ),
        generateChargesWithAILoading: jobToolFeature.selectAiChargesLoading,
        hasInvalidNewEvents: createSelector(
            selectJob,
            jobWorkordersFeature.selectWorkordersEditor,
            (job, { workorders }) => workorders
                .filter((wo) => wo.new && !wo.removed)
                .some((wo) => !validateLocationsForEventType(job?.locations || [], wo.type).isValid)
        ),
        jobNotInZone: createSelector(selectJobInCurrentZone, (inZone) => !inZone),
        // Requirement: #6142 - Crew assignment from workorders is in testing phase — restricted to ymm-interstate-usa zone only
        crewAssignmentEnabled: createSelector(
            zonesFeature.selectCurrentAppZone,
            (zone) => zone?.id === 'ymm-interstate-usa'
        ),
    }));

    ngOnDestroy(): void {
        this.resizeObserver.disconnect();
        this.subs.unsubscribe();

        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }

    ngAfterViewInit(): void {
        this.watchResize();
        this.scrollToWorkorder();
    }

    trackByEventId(index: number, workorder: any): any {
        return workorder?.eventId ?? index;
    }

    trackByChargeId(index: number, charge: any): any {
        return charge?.id ?? index;
    }

    trackByAppliedDiscountId(index: number, discount: any) {
        return discount?.appliedId ?? index;
    }

    openCharge(job: FullJobFragment, charge?: Charge) {
        if (!charge) {
            return;
        }

        this.detailsHelper.open('charge', {
            ...charge,
            job,
        });
    }

    openDiscount(discount: Discount) {
        if (!discount) {
            return;
        }

        this.detailsHelper.open('discount', discount);
    }

    watchResize() {
        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                this.width = entry.contentRect.width;
            }
        });

        this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    scrollToWorkorder() {
        const selectedWorkorder$ = this.route.queryParamMap.pipe(
            map(params => params.get('workorderId')),
            distinctUntilChanged()
        );

        const renderedWorkorders$ = this.workorderQueryList.changes.pipe(
            startWith(this.workorderQueryList)
        );

        this.subs.sink = combineLatest([ selectedWorkorder$, renderedWorkorders$ ]).pipe(
            map(([ workorderId, workorders ]) => workorders?.find((ref) => ref.nativeElement.dataset['id'] === workorderId)),
            filter(Boolean),
            switchMap((workorderRef: ElementRef<HTMLElement>) => this.ngZone.onStable.pipe(
                take(1),
                tap(() => {
                  this.intersectionObserver?.disconnect();
                  this.intersectionObserver = scrollIntoViewAndHighlight(workorderRef.nativeElement);
                }),
            )),
        ).subscribe();
    }
}
